import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import styled from "styled-components";
import { Toast } from "ratel-ui";
import Layout from "../components/Layout";
import Schedule from "../components/Schedule";
import ConfirmModal from "../components/ConfirmModal";
import { IconCalendar, IconCheck, IconClock, IconMail, IconSheet, IconStar, IconStopwatch } from "../components/Icons";
import { C, F, CARD_RING, ring, pulsing, Container, Section, H1, H2, ScanLines, InfoChip } from "../styles";
import { AREAS, getArea } from "../data/calendar";
import { answeredCount, firstOpenAt, getStatus } from "../utils/status";
import { countdownParts, useNow } from "../utils/time";
import useStudent from "../utils/useStudent";
import { startSimulado } from "../service";

// ---------- Antes de abrir ----------

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  background: ${C.navy};
  box-shadow: ${CARD_RING};
  padding: clamp(32px, 5vw, 56px) clamp(20px, 4vw, 48px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  text-align: center;
  > * {
    position: relative;
  }
`;

const SignalTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 15px;
  border-radius: 20px;
  background: rgba(7, 10, 51, 0.82);
  box-shadow: ${ring(1, C.orange)};
  font: 600 13px/1.25 ${F.title};
  color: ${C.white};
  i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${C.orange};
    ${pulsing}
  }
`;

const HeroTitle = styled(H1)`
  font-size: clamp(28px, 3.6vw, 44px);
  line-height: 1.1;
`;

const Row = styled.div`
  display: flex;
  gap: ${(p) => p.gap || 10}px;
  flex-wrap: wrap;
  justify-content: center;
`;

const CountBox = styled.div`
  min-width: clamp(64px, 17vw, 84px);
  padding: 12px 10px;
  border-radius: 12px;
  background: ${C.ink};
  box-shadow: ${ring(1, "rgba(255,255,0,0.35)")};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  b {
    font: 400 clamp(34px, 4vw, 48px) / 1 ${F.num};
    color: ${C.yellow};
    font-variant-numeric: tabular-nums;
  }
  span {
    font: 600 11px/1 ${F.title};
    letter-spacing: 0.12em;
    color: ${C.pale};
  }
`;

function BeforeOpen({ now, openAt, items }) {
  return (
    <Section>
      <Hero>
        <ScanLines />
        <SignalTag>
          <i />
          SINAL EM BREVE
        </SignalTag>
        <HeroTitle>O primeiro simulado entra no ar em</HeroTitle>
        <Row role="timer" aria-live="off">
          {countdownParts(openAt, now).map((u) => (
            <CountBox key={u.l}>
              <b>{u.v}</b>
              <span>{u.l}</span>
            </CountBox>
          ))}
        </Row>
        <Row gap={8}>
          <InfoChip>
            <IconClock />
            <span>
              <strong>Libera às 00h</strong> da data indicada
            </span>
          </InfoChip>
          <InfoChip>
            <IconCalendar />
            <span>
              <strong>Fica no ar</strong> até o último dia do período
            </span>
          </InfoChip>
          <InfoChip>
            <IconMail />
            <span>
              <strong>Aviso</strong> por e-mail e no WhatsApp
            </span>
          </InfoChip>
        </Row>
      </Hero>
      <Section gap={14}>
        <H2 as="span">Cronograma</H2>
        <Schedule items={items} onStart={() => {}} onSeeGabarito={() => {}} />
      </Section>
    </Section>
  );
}

// ---------- Início (com e sem login) ----------

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 560px;
`;

const Done = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  span {
    font: 600 13px/1 ${F.title};
    color: ${C.pale};
  }
`;

const Bar = styled.div`
  width: ${(p) => p.width || "140px"};
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
  div {
    height: 100%;
    width: ${(p) => p.pct}%;
    background: ${C.yellow};
    border-radius: 3px;
    transition: width 240ms;
  }
`;

const Lead = styled.span`
  font-size: 15px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
`;

const Rules = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 28px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font: 600 14px/1.2 ${F.title};
    color: rgba(255, 255, 255, 0.88);
  }
`;

export default function SimuladosPage() {
  const history = useHistory();
  const location = useLocation();
  const now = useNow();
  const { user, progress, periods, loading } = useStudent();
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const items = useMemo(() => {
    const out = {};
    AREAS.forEach((a) => {
      const p = progress[a.slug];
      out[a.slug] = {
        status: getStatus(a, now, progress, !!user, periods),
        answered: user ? answeredCount(progress, a.slug) : 0,
        score: p ? p.score : null,
      };
    });
    return out;
  }, [now, progress, user, periods]);

  const openAt = firstOpenAt(periods);
  const beforeOpen = now < new Date(openAt).getTime();
  const doneCount = AREAS.filter((a) => items[a.slug].status === "done").length;

  const toLogin = (next) => history.push(`/login?next=${encodeURIComponent(next)}`);

  const onStart = (area) => {
    if (!user) return toLogin(`/simulados?comecar=${area.slug}`);
    if (items[area.slug].answered > 0) return history.push(`/simulados/${area.slug}`);
    setConfirm(area);
  };

  const onSeeGabarito = (area) => {
    const next = `/gabarito?area=${area.slug}`;
    if (!user) return toLogin(next);
    history.push(next);
  };

  // Volta do login com ?comecar=slug: segue a ação que o aluno clicou.
  const pending = new URLSearchParams(location.search).get("comecar");
  useEffect(() => {
    if (!pending || loading) return;
    history.replace("/simulados");
    const area = getArea(pending);
    if (area && user && items[area.slug].status === "live") onStart(area);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, loading]);

  const onConfirm = async () => {
    setBusy(true);
    try {
      await startSimulado(confirm.slug);
      history.push(`/simulados/${confirm.slug}`);
    } catch (e) {
      setBusy(false);
      Toast.error(e.message);
    }
  };

  return (
    <Layout title="Simulados" user={user} action={beforeOpen ? "none" : "auto"}>
      <Container>
        {loading ? null : beforeOpen ? (
          <BeforeOpen now={now} openAt={openAt} items={items} />
        ) : (
          <Section gap={32}>
            <Intro>
              <H1>Pronto pra entrar no ar?</H1>
              {user ? (
                <Done>
                  <Bar pct={(doneCount / AREAS.length) * 100}>
                    <div />
                  </Bar>
                  <span>
                    {doneCount} de {AREAS.length} feitos
                  </span>
                </Done>
              ) : (
                <Lead>Entre com a sua inscrição pra ver o seu progresso e o seu gabarito.</Lead>
              )}
            </Intro>
            <Schedule items={items} onStart={onStart} onSeeGabarito={onSeeGabarito} />
            <Rules>
              <span>
                <IconSheet />
                20 questões por área
              </span>
              <span>
                <IconStopwatch />
                Sem cronômetro, dá pra sair e voltar
              </span>
              <span>
                <IconStar />
                Uma tentativa por simulado
              </span>
              <span>
                <IconCheck color={C.yellow} />
                Gabarito na hora
              </span>
            </Rules>
          </Section>
        )}
      </Container>
      {confirm && <ConfirmModal area={confirm} busy={busy} onConfirm={onConfirm} onClose={() => setConfirm(null)} />}
    </Layout>
  );
}
